import { type BrowserOptions, type ErrorEvent, type NodeOptions } from '@sentry/nextjs'
import stripAnsi from 'strip-ansi'
import {
  APP_VERSION,
  COMMIT_SHA,
  IS_PREVIEW_ENV,
  IS_PRODUCTION_DEPLOYMENT,
  IS_STAGING_DEPLOYMENT,
  SENTRY_DSN,
} from '@/config/environments.config'

const IGNORED_ERRORS = [
  // Non-critical errors
  /Hydration failed/,

  // Expected outcomes we already surface to the user in the UI - not defects.
  // The `ErrorModal: <ErrorType>` variants are no longer sent at all: ErrorModal skips
  // EXPECTED_ERROR_TYPES (types/errors.types.ts) instead of reporting then filtering.
  /is not a valid code/,
  '404: No results found',

  // Third-party browser noise - not our code, not fixable from here.
  // WebView teardown when an in-app browser closes mid-page:
  'Error invoking postMessage: Java object is gone',
  // Injected extension scripts (stacks are webkit-masked-url:// only):
  'Can\'t find variable: data',
  '$ is not defined',
  'top.GLOBALS',
  'fb_xd_fragment',

  // Expected Checkout response
  /intent_requires_action/,
]

const getEnv = (): 'production' | 'preview' | 'development' | 'staging' => {
  // Environment variable-based determination
  if (IS_PRODUCTION_DEPLOYMENT) {
    return 'production'
  }

  if (IS_PREVIEW_ENV) {
    return 'preview'
  }

  if (IS_STAGING_DEPLOYMENT) {
    return 'staging'
  }

  // Default fallback
  return 'development'
}

export const sentryConfig: NodeOptions | BrowserOptions = {
  tracesSampleRate: 0.005, // Sample 0.5% of transactions
  sampleRate: 0.5, // Sample 50% of errors
  dsn: SENTRY_DSN,
  release: COMMIT_SHA,
  environment: getEnv(),
  // Surface the package.json version (commit SHA remains the release identifier).
  // Tags reach errors; attributes reach streamed spans, which no longer get scope tags in v11.
  // eslint-disable-next-line camelcase
  initialScope: { tags: { app_version: APP_VERSION }, attributes: { app_version: APP_VERSION } },
  // v11 collects everything by default. Keep checkout PII out: headers and query params only,
  // on top of the SDK's built-in filter (auth, token, secret, session, cookie, key, ...).
  dataCollection: {
    userInfo: false,
    cookies: false,
    httpHeaders: { deny: ['forwarded', '-ip', 'remote-', 'via', '-user'] },
    httpBodies: [],
    // `code` covers promo codes (`code[]`) and the Auth0 callback, `state` the OAuth state
    urlQueryParams: { deny: ['code', 'state', 'email', 'phone', 'name'] },
    stackFrameVariables: false,
  },
  beforeSend: (event) => ({
    ...event,
    message: event.message ? stripAnsi(event.message) : undefined,
  } satisfies ErrorEvent),
  ignoreSpans: [
    /getNonRecurringEventById/,
  ],
  ignoreErrors: IGNORED_ERRORS,
  denyUrls: [
    // Pinterest scripts
    /pinterest\.com/i,
    /.*\.?pinterest\.com/i,
    // Facebook flakiness
    /graph\.facebook\.com/i,
    // Facebook blocked
    /connect\.facebook\.net\/en_US\/all\.js/i,
    // Chrome extensions
    /extensions\//i,
    /^chrome:\/\//i,
    /ad\.doubleclick\.net/i,
    /widget\.usersnap\.com/i,
    // Bing!
    /bat.bing.com/i,
  ],
}
